/**
 * 字段名转换工具
 * 用于将 API 响应的字段名从 snake_case 转换为 camelCase
 */

/**
 * 将蛇形命名转换为驼峰命名
 * @example
 * snakeToCamel('user_name') => 'userName'
 * snakeToCamel('is_active') => 'isActive'
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * 将驼峰命名转换为蛇形命名
 * @example
 * camelToSnake('userName') => 'user_name'
 * camelToSnake('isActive') => 'is_active'
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

/**
 * 深度转换对象的所有键名为驼峰格式
 * 支持嵌套对象和数组
 */
export function transformKeysToCamel<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  // 处理数组
  if (Array.isArray(obj)) {
    return obj.map(item => transformKeysToCamel(item)) as T
  }

  // 处理 Date 对象
  if (obj instanceof Date) {
    return obj as T
  }

  // 处理普通对象
  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {}

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = snakeToCamel(key)
        transformed[camelKey] = transformKeysToCamel(obj[key])
      }
    }

    return transformed as T
  }

  // 其他类型直接返回
  return obj as T
}

/**
 * 深度转换对象的所有键名为蛇形格式
 * 支持嵌套对象和数组
 */
export function transformKeysToSnake<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  // 处理数组
  if (Array.isArray(obj)) {
    return obj.map(item => transformKeysToSnake(item)) as T
  }

  // 处理 Date 对象
  if (obj instanceof Date) {
    return obj as T
  }

  // 处理普通对象
  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {}

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = camelToSnake(key)
        transformed[snakeKey] = transformKeysToSnake(obj[key])
      }
    }

    return transformed as T
  }

  // 其他类型直接返回
  return obj as T
}

/**
 * 判断字符串是否为 JSON 格式
 */
export function isJsonString(str: string): boolean {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

/**
 * 安全地解析 JSON 字符串或返回已解析的对象
 */
export function safeJsonParse<T = any>(input: string | T, defaultValue: T | null = null): T | null {
  // 如果已经是对象，直接返回
  if (input !== null && typeof input === 'object') {
    return input as T
  }
  // 如果是空字符串，返回默认值
  if (typeof input === 'string' && input.trim() === '') {
    return defaultValue
  }
  try {
    return JSON.parse(input as string) as T
  } catch {
    return defaultValue
  }
}
